import { HasVisibleDescendantsChildrenParams, HasVisibleDescendantsHiddenParams, isMarketHidden } from "@/services/market.service";
import { RootMarketNodes } from "@/types/ApiTypes";
import { invertedMarketFolderLabelMapping } from '../DodConfigEditor/common/utils';
import { getRegexForSearchString } from '@/utils';

export type FilterMarketNodesParams = {
    rootMarketNodes: RootMarketNodes;
    hasVisibleDescendants: (
        node: MarketNode, 
        getChildrenParams: HasVisibleDescendantsChildrenParams,
        isHiddenParams: HasVisibleDescendantsHiddenParams
    ) => Promise<boolean>;
    getChildrenParams: {
        sku: string;
        categories: string[];
        runType: RunType;
        purchasedMarketKeys?: string[];
        isDodMarketPicker: boolean;
        hasSummedCategories?: boolean;
        summedCategories?: string[];
        nonSummedCategories?: string[] | 'all';
        isMarketSummedMode?: boolean;
        isEnableParentCompanyMode?: boolean;
    };
    isHiddenParams: {
        includePseudoRemainingMarkets?: boolean;
        requiredMarketGroup?: string;
        requiredMasterCompany?: string;
        requireRemainingMarket?: boolean;
        showRemainingMarkets?: boolean;
        hideDisabledMarkets?: boolean;
        hideNonPromoMarkets?: boolean;
        enableLimitedMarketSubscription?: boolean;
        accessibleMasterCompanies?: string[];
        purchasedMarketKeys?: string[];
    };
};

export async function filterMarketNodesByVisibility({
    rootMarketNodes,
    hasVisibleDescendants,
    getChildrenParams,
    isHiddenParams,
}: FilterMarketNodesParams): Promise<RootMarketNodes> {
    const newFilteredNodes: RootMarketNodes = {};
    const rootNodeKeys = Object.keys(rootMarketNodes) as (keyof RootMarketNodes)[];

    for (const key of rootNodeKeys) {
        const nodes = rootMarketNodes[key];
        if (nodes) {
            const visibilityPromises = nodes.map(async (node) => {
                // If it's a selectable market (not a folder), check if it's hidden.
                if (!node.hasChildren) {
                    return !isMarketHidden(node, isHiddenParams);
                }
                // If it's a folder, recursively check for ANY visible descendants at any depth
                return await hasVisibleDescendants(node, getChildrenParams, isHiddenParams);
            });
            
            const visibilityResults = await Promise.all(visibilityPromises);
            
            const visibleNodes = nodes.filter((_, index) => visibilityResults[index]);
            
            if (visibleNodes.length > 0) {
                newFilteredNodes[key] = visibleNodes;
            }
        }
    }

    return newFilteredNodes;
}

export function filterMarketsByType(
    markets: Partial<Record<keyof RootMarketNodes, MarketNode[]>>,
    filterType: string
): Partial<Record<keyof RootMarketNodes, MarketNode[]>> {
    return Object.entries(markets).reduce((acc, [key, value]) => {
        if (value) {
            const filteredByFolder = value.filter(
                (market) => market.subMrktType === invertedMarketFolderLabelMapping[filterType]
            );
            if (filteredByFolder.length > 0) {
                acc[key as keyof RootMarketNodes] = filteredByFolder;
            }
        }
        return acc;
    }, {} as Partial<Record<keyof RootMarketNodes, MarketNode[]>>);
}

export function filterMarketsByText(
    markets: Partial<Record<keyof RootMarketNodes, MarketNode[]>>,
    filterText: string
): Partial<Record<keyof RootMarketNodes, MarketNode[]>> {
    const matcher = getRegexForSearchString(filterText);
    return Object.entries(markets).reduce((acc, [key, value]) => {
        if (value) {
            const filteredByText = value.filter(({ name }) =>
                matcher.test(name.trim())
            );
            if (filteredByText.length > 0) {
                acc[key as keyof RootMarketNodes] = filteredByText;
            }
        }
        return acc;
    }, {} as Partial<Record<keyof RootMarketNodes, MarketNode[]>>);
}

export type ApplyAllFiltersParams = FilterMarketNodesParams & {
    filterText?: string;
    filterType?: string;
    enableLimitedMarketSubscription?: boolean;
};

/**
 * Applies all market filtering logic: visibility, type, and text filtering
 */
export async function applyAllMarketFilters({
    rootMarketNodes,
    hasVisibleDescendants,
    getChildrenParams,
    isHiddenParams,
    filterText,
    filterType,
    enableLimitedMarketSubscription,
}: ApplyAllFiltersParams): Promise<Partial<Record<keyof RootMarketNodes, MarketNode[]>>> {
    // Step 1: Filter by visibility (hide empty folders)
    let filteredMarkets = await filterMarketNodesByVisibility({
        rootMarketNodes,
        hasVisibleDescendants,
        getChildrenParams,
        isHiddenParams,
    });

    // Step 2: Filter by type (if applicable)
    if (enableLimitedMarketSubscription && filterType && filterType !== 'all') {
        filteredMarkets = filterMarketsByType(filteredMarkets, filterType);
    }

    // Step 3: Filter by text search (if applicable)
    if (filterText) {
        filteredMarkets = filterMarketsByText(filteredMarkets, filterText);
    }

    return filteredMarkets;
}